import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  DollarSign, AlertTriangle, ShoppingCart, Package,
  Instagram, Youtube, BarChart3,
  ChevronRight, ChevronLeft, RefreshCw, CheckCircle, TrendingUp,
} from 'lucide-react';
import {
  ComposedChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { SectionCard, DataTableSkeleton } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_DASHBOARD } from '../demo/data';

// ── Types ──────────────────────────────────────────────────────────────────

interface TopInfluencer {
  influencer_id: string;
  slug: string;
  stage_name: string;
  platform: string | null;
  category: string | null;
  follower_count: number | null;
  engagement_rate: number | null;
  profile_image_url: string | null;
  orders: number;
  revenue: number;
  forecast_gmv?: number;
  approved?: boolean;
}

interface RestockItem {
  product_title?: string;
  title?: string;
  current_stock?: number;
  min_stock?: number;
  id?: string;
  moq?: number | null;
  has_alert?: boolean;
  alert_message?: string | null;
}

interface TrendingProduct {
  name: string;
  category: string;
  target_ddp: number;
  market_price: number;
  supplier: string;
  image_url: string;
  trend_pct: number;
}

interface DashboardData {
  total_products: number;
  pending_orders: number;
  total_gmv: number;
  total_sales: number;
  fulfillment_rate: number;
  low_stock_count: number;
  orders_by_status: Record<string, number>;
  top_products: Array<{ title: string; units: number; revenue: number }>;
  recent_orders: Array<{ id: number; order_number: string; status: string; total_amount: string; created_at: string }>;
  revenue_by_day: Array<{ date: string; revenue: number; orders: number }>;
  top_influencers: TopInfluencer[];
  restock_needed: RestockItem[];
  trending_products?: TrendingProduct[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtCurrency(n: number, lang: string) {
  const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 1 })}M`;
  if (n >= 1_000) return `$${(n / 1_000).toLocaleString(locale, { maximumFractionDigits: 1 })}K`;
  return `$${n.toLocaleString(locale)}`;
}

function fmtCurrencyFull(n: number, lang: string) {
  const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
  return `$${n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtFollowers(n: number | null) {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function PlatformIcon({ platform, size = 'w-3 h-3' }: { platform: string | null; size?: string }) {
  if (platform === 'instagram') return <Instagram className={size} />;
  if (platform === 'youtube') return <Youtube className={size} />;
  if (platform === 'tiktok') return (
    <svg className={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.3 6.3 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.76a8.21 8.21 0 0 0 4.79 1.52V6.84a4.85 4.85 0 0 1-1.02-.15z" />
    </svg>
  );
  return <BarChart3 className={size} />;
}

const STATUS_COLORS: Record<string, string> = {
  new: '#FFD400', processing: '#a78bfa', shipped: '#38bdf8',
  delivered: '#4ade80', cancelled: '#f87171', exception: '#fb923c',
};

function getNextPayoutDate(): Date {
  const now = new Date();
  const next15 = new Date(now.getFullYear(), now.getMonth(), 15);
  return now.getTime() >= next15.getTime()
    ? new Date(now.getFullYear(), now.getMonth() + 1, 15)
    : next15;
}

// ── Recharts Tooltips ──────────────────────────────────────────────────────

function DualLineTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 text-xs" style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
      <p className="text-white/50 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.stroke || p.color }} className="font-semibold">
          ${(p.value ?? 0).toLocaleString()}
        </p>
      ))}
    </div>
  );
}

function StatusTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 text-xs" style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
      <p className="text-white/50 capitalize mb-0.5">{label}</p>
      <p className="text-white font-semibold">{payload[0]?.value} orders</p>
    </div>
  );
}

// ── CreatorMatchCard (carousel card) ───────────────────────────────────────

function CreatorMatchCard({ inf, lang, t, onApprove }: {
  inf: TopInfluencer; lang: string;
  t: ReturnType<typeof useTranslation>['t'];
  onApprove: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const avatarUrl = inf.profile_image_url
    || `https://api.dicebear.com/8.x/avataaars/svg?seed=${encodeURIComponent(inf.stage_name)}`;
  const diceBearFallback = `https://api.dicebear.com/8.x/avataaars/svg?seed=${encodeURIComponent(inf.stage_name)}`;
  const initials = inf.stage_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const imgSrc = imgError ? diceBearFallback : avatarUrl;
  const [fallbackError, setFallbackError] = useState(false);

  return (
    <div
      className="shrink-0 flex flex-col p-4"
      style={{
        width: 'calc(25% - 9px)',
        minWidth: 200,
        backgroundColor: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 4,
        scrollSnapAlign: 'start',
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: 'rgba(255,212,0,0.12)', border: '1.5px solid rgba(255,212,0,0.2)' }}>
          {!fallbackError ? (
            <img
              src={imgSrc} alt={inf.stage_name}
              className="w-full h-full object-cover"
              onError={() => { if (!imgError) setImgError(true); else setFallbackError(true); }}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="text-xs font-bold" style={{ color: '#FFD400' }}>{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{inf.stage_name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {inf.platform && (
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>
                <PlatformIcon platform={inf.platform} />
              </span>
            )}
            <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {fmtFollowers(inf.follower_count)} {t('vendor:dashboard.followers')}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-2">
        {inf.category && (
          <span className="inline-block text-[10px] px-1.5 py-0.5 capitalize"
            style={{ backgroundColor: 'rgba(167,139,250,0.1)', color: '#a78bfa', borderRadius: 2 }}>
            {inf.category}
          </span>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('vendor:dashboard.forecastGmv')}
          </span>
          <span className="text-sm font-bold text-white font-display">
            {inf.forecast_gmv ? fmtCurrency(inf.forecast_gmv, lang) : '—'}
          </span>
        </div>
        {inf.engagement_rate != null && (
          <div className="flex items-center justify-between">
            <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('vendor:dashboard.engRate')}
            </span>
            <span className="text-sm text-white/70">
              {(inf.engagement_rate * 100).toFixed(1)}%
            </span>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {inf.approved ? (
          <div className="flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold"
            style={{ color: '#4ade80' }}>
            <CheckCircle className="w-3.5 h-3.5" />
            {t('vendor:dashboard.approved')}
          </div>
        ) : (
          <button
            onClick={onApprove}
            className="w-full py-1.5 text-xs font-semibold transition-colors"
            style={{
              backgroundColor: '#FFD400',
              color: '#0B0B0B',
              borderRadius: 2,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t('vendor:dashboard.approve')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function VendorDashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation(['vendor', 'common']);
  const { isDemoMode } = useDemo();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [approvedSet, setApprovedSet] = useState<Set<string>>(new Set());
  const lang = i18n.language;
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isDemoMode) {
      setData(DEMO_VENDOR_DASHBOARD as unknown as DashboardData);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/vendor/dashboard`, { credentials: 'include' });
        const json = await res.json();
        if (res.ok) setData(json.data);
      } catch { /* no-op */ }
      finally { setLoading(false); }
    })();
  }, [isDemoMode]);

  const vendorName = user?.vendor?.name || t('common:vendor');

  const derived = useMemo(() => {
    if (!data) return { nextPayout: 0, payoutDate: getNextPayoutDate() };
    const rev = data.revenue_by_day;
    const avgDaily = rev.length > 0 ? rev.reduce((s, r) => s + r.revenue, 0) / rev.length : 0;
    const monthly = Math.round(avgDaily * 30);
    return {
      nextPayout: Math.round(monthly * 0.85),
      payoutDate: getNextPayoutDate(),
    };
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const rev = data.revenue_by_day;
    if (rev.length === 0) return [];

    const window = 7;
    return rev.map((r, i) => {
      const start = Math.max(0, i - window + 1);
      const slice = rev.slice(start, i + 1);
      const avg = slice.reduce((s, x) => s + x.revenue, 0) / slice.length;
      return {
        date: r.date.slice(5),
        revenue: r.revenue,
        forecast: Math.round(avg),
      };
    });
  }, [data]);

  const handleApprove = (id: string) => {
    setApprovedSet(prev => new Set([...prev, id]));
  };

  const scrollCarousel = (dir: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild?.clientWidth ?? 240;
    el.scrollBy({ left: dir === 'right' ? cardWidth + 12 : -(cardWidth + 12), behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white font-display">
            {t('vendor:dashboard.welcome')}, {vendorName}
          </h1>
        </div>
        <DataTableSkeleton rows={6} cols={3} />
      </div>
    );
  }

  const d: DashboardData = data || {
    total_products: 0, pending_orders: 0, total_gmv: 0, total_sales: 0,
    fulfillment_rate: 0, low_stock_count: 0, orders_by_status: {},
    top_products: [], recent_orders: [], revenue_by_day: [],
    top_influencers: [], restock_needed: [],
  };

  const toFulfill = (d.orders_by_status['new'] || 0) + (d.orders_by_status['processing'] || 0);

  const statusBarData = Object.entries(d.orders_by_status)
    .map(([status, count]) => ({ status: status.charAt(0).toUpperCase() + status.slice(1), count }))
    .sort((a, b) => b.count - a.count);

  const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
  const payoutDateStr = derived.payoutDate.toLocaleDateString(locale, { month: '2-digit', day: '2-digit', year: 'numeric' });

  const influencers = d.top_influencers.map(inf => ({
    ...inf,
    approved: inf.approved || approvedSet.has(inf.influencer_id),
  }));

  const trendingProducts: TrendingProduct[] = d.trending_products || (DEMO_VENDOR_DASHBOARD as any).trending_products || [];

  return (
    <div className="space-y-5 max-w-7xl mx-auto">

      {/* ── Row 1: Welcome + Payout pill ──────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-display">
            {t('vendor:dashboard.welcome')}, {vendorName}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('vendor:dashboard.subtitle')}
          </p>
        </div>

        <div className="shrink-0 px-5 py-3"
          style={{
            backgroundColor: 'rgba(255,212,0,0.06)',
            border: '1px solid rgba(255,212,0,0.15)',
            borderRadius: 4,
          }}>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4" style={{ color: '#FFD400' }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>{t('vendor:dashboard.commissionPayout')}:</span>
            <span className="font-bold text-white font-display">{fmtCurrencyFull(derived.nextPayout, lang)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm mt-1">
            <span className="w-4" />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>{t('vendor:dashboard.payoutDate')}:</span>
            <span className="font-semibold text-white/80">{payoutDateStr}</span>
          </div>
        </div>
      </div>

      {/* ── Alert banners ─────────────────────────────────────────── */}
      {(toFulfill > 0 || d.low_stock_count > 0) && (
        <div className="flex flex-wrap gap-2">
          {toFulfill > 0 && (
            <Link to="/vendor/orders"
              className="flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:opacity-80"
              style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171', borderRadius: 2 }}>
              <ShoppingCart className="w-3.5 h-3.5" />
              {t('vendor:dashboard.ordersNeedFulfillment', { count: toFulfill })}
            </Link>
          )}
          {d.low_stock_count > 0 && (
            <Link to="/vendor/products"
              className="flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:opacity-80"
              style={{ backgroundColor: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.18)', color: '#fb923c', borderRadius: 2 }}>
              <AlertTriangle className="w-3.5 h-3.5" />
              {t('vendor:dashboard.productsNeedRestocking', { count: d.low_stock_count })}
            </Link>
          )}
        </div>
      )}

      {/* ── Row 2: Creator Matches carousel ───────────────────────── */}
      <SectionCard
        title={t('vendor:dashboard.creatorMatches')}
        actions={
          <Link to="/vendor/creators" className="flex items-center gap-1 text-xs transition-colors hover:opacity-80" style={{ color: '#FFD400' }}>
            {t('vendor:dashboard.viewAllCreators')} <ChevronRight className="w-3 h-3" />
          </Link>
        }
        noPadding
      >
        {influencers.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>
            {t('vendor:dashboard.noCreatorMatches')}
          </div>
        ) : (
          <div className="relative px-5 pb-5 pt-3">
            <button
              onClick={() => scrollCarousel('left')}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-7 h-7 flex items-center justify-center transition-colors"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div
              ref={carouselRef}
              className="flex gap-3 overflow-x-auto hide-scrollbar"
              style={{ scrollSnapType: 'x mandatory', scrollBehavior: 'smooth' }}
            >
              {influencers.map(inf => (
                <CreatorMatchCard
                  key={inf.influencer_id}
                  inf={inf}
                  lang={lang}
                  t={t}
                  onApprove={() => handleApprove(inf.influencer_id)}
                />
              ))}
            </div>

            <button
              onClick={() => scrollCarousel('right')}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-7 h-7 flex items-center justify-center transition-colors"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </SectionCard>

      {/* ── Row 3: Chart (3/5) + Top Products (2/5) ───────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <SectionCard title={t('vendor:dashboard.todaySalesAndForecast')}>
            {chartData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm" style={{ color: 'rgba(255,255,255,0.2)' }}>
                {t('vendor:dashboard.noRevenueData')}
              </div>
            ) : (
              <>
                <div className="-mx-3 -mb-3" style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="vendorRevGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FFD400" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#FFD400" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }}
                        tickLine={false} axisLine={false} interval={6} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 10 }}
                        tickLine={false} axisLine={false}
                        tickFormatter={(v: number) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                      <Tooltip content={<DualLineTooltip />} />
                      <Line type="monotone" dataKey="revenue" stroke="#FFD400" strokeWidth={2}
                        dot={false} name={t('vendor:dashboard.actualRevenue')} />
                      <Line type="monotone" dataKey="forecast" stroke="#a78bfa" strokeWidth={1.5}
                        strokeDasharray="6 3" dot={false} name={t('vendor:dashboard.forecastRevenue')} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-5 pt-3 mt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-0.5" style={{ backgroundColor: '#FFD400' }} />
                    <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('vendor:dashboard.actualRevenue')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-0.5" style={{ backgroundColor: '#a78bfa', backgroundImage: 'repeating-linear-gradient(90deg, #a78bfa 0, #a78bfa 4px, transparent 4px, transparent 7px)' }} />
                    <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('vendor:dashboard.forecastRevenue')}</span>
                  </div>
                </div>
              </>
            )}
          </SectionCard>
        </div>

        <div className="lg:col-span-2">
          <SectionCard
            title={t('vendor:dashboard.topProducts')}
            subtitle={t('vendor:dashboard.topProductsSubtitle')}
            actions={
              <Link to="/vendor/products" className="flex items-center gap-1 text-xs transition-colors hover:opacity-80" style={{ color: '#FFD400' }}>
                {t('vendor:dashboard.viewAll')} <ChevronRight className="w-3 h-3" />
              </Link>
            }
            noPadding
          >
            {d.top_products.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>
                {t('vendor:dashboard.noProductSales')}
              </div>
            ) : (
              <div className="px-5 pb-3">
                {d.top_products.map((p, i) => {
                  const maxRev = d.top_products[0]?.revenue || 1;
                  return (
                    <div key={i} className="py-2.5"
                      style={{ borderBottom: i < d.top_products.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-mono w-4 shrink-0" style={{ color: 'rgba(255,255,255,0.2)' }}>{i + 1}</span>
                        <span className="text-sm text-white/80 flex-1 truncate">{p.title}</span>
                        <span className="text-sm font-semibold text-white shrink-0">{fmtCurrency(p.revenue, lang)}</span>
                      </div>
                      <div className="ml-6 flex items-center gap-2">
                        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="h-full rounded-full" style={{ width: `${(p.revenue / maxRev) * 100}%`, backgroundColor: '#FFD400', opacity: 0.7 }} />
                        </div>
                        <span className="text-[11px] shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }}>
                          {p.units.toLocaleString(locale)} {t('vendor:dashboard.units')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── Row 4: Trending Competitor Market Products ─────────────── */}
      <SectionCard title={t('vendor:dashboard.trendingCompetitorProducts')}>
        {trendingProducts.length === 0 ? (
          <div className="py-8 text-center text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>
            {t('vendor:dashboard.noTrendingData')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 -mx-1">
            {trendingProducts.map((tp, i) => (
              <div key={i} className="p-4 flex flex-col gap-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 4,
                }}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                    <Package className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{tp.name}</p>
                    <span className="inline-block text-[10px] px-1.5 py-0.5 mt-1 capitalize"
                      style={{ backgroundColor: 'rgba(56,189,248,0.1)', color: '#38bdf8', borderRadius: 2 }}>
                      {tp.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0 text-xs font-semibold" style={{ color: '#4ade80' }}>
                    <TrendingUp className="w-3 h-3" />
                    {t('vendor:dashboard.trendUp', { pct: tp.trend_pct })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {t('vendor:dashboard.targetDdp')}
                    </p>
                    <p className="text-sm font-bold text-[#FFD400] font-display">
                      ${tp.target_ddp.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {t('vendor:dashboard.marketPrice')}
                    </p>
                    <p className="text-sm font-semibold text-white">
                      ${tp.market_price.toFixed(2)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {t('vendor:dashboard.supplier')}
                    </p>
                    <p className="text-xs text-white/60 truncate">{tp.supplier}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* ── Row 5: Alerts/Restock + Orders by Status ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title={t('vendor:dashboard.restockInventory')}
          subtitle={t('vendor:dashboard.restockSubtitle')}
          noPadding
        >
          {(toFulfill > 0 || d.low_stock_count > 0) && (
            <div className="px-5 pt-4 pb-2 space-y-2">
              {d.low_stock_count > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs"
                  style={{ backgroundColor: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.12)', borderRadius: 2, color: '#fb923c' }}>
                  <Package className="w-3 h-3 shrink-0" />
                  {t('vendor:dashboard.lowStockAlert', { count: d.low_stock_count })}
                </div>
              )}
              {toFulfill > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs"
                  style={{ backgroundColor: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.12)', borderRadius: 2, color: '#f87171' }}>
                  <ShoppingCart className="w-3 h-3 shrink-0" />
                  {t('vendor:dashboard.pendingAlert', { count: toFulfill })}
                </div>
              )}
            </div>
          )}

          {d.restock_needed.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>
              {t('vendor:dashboard.allStocked')}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
              {d.restock_needed.map((item, i) => {
                const name = item.product_title || (item as any).title || '';
                const isAlert = item.has_alert
                  ?? (item.current_stock != null && item.min_stock != null && item.current_stock < item.min_stock);
                const detail = item.alert_message
                  || (item.current_stock != null ? `${item.current_stock} / ${item.min_stock} in stock` : null)
                  || (item.moq ? t('vendor:dashboard.minOrder', { count: item.moq }) : t('vendor:dashboard.checkInventoryNote'));

                return (
                  <div key={item.id || i} className="px-5 py-3 flex items-start gap-3">
                    <div className="mt-0.5 shrink-0">
                      {isAlert
                        ? <AlertTriangle className="w-3.5 h-3.5 text-[#fb923c]" />
                        : <RefreshCw className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.2)' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/80 truncate">{name}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{detail}</p>
                    </div>
                    {isAlert && (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 font-semibold"
                        style={{ backgroundColor: 'rgba(251,146,60,0.1)', color: '#fb923c', borderRadius: 2, border: '1px solid rgba(251,146,60,0.18)' }}>
                        {t('vendor:dashboard.alerts')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        <SectionCard
          title={t('vendor:dashboard.ordersByStatus')}
          actions={
            <Link to="/vendor/orders" className="text-xs" style={{ color: '#FFD400' }}>
              {t('vendor:dashboard.viewAll')} →
            </Link>
          }
        >
          {statusBarData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm" style={{ color: 'rgba(255,255,255,0.2)' }}>
              {t('vendor:dashboard.noOrdersStatus')}
            </div>
          ) : (
            <>
              <div className="-mx-3 -mb-1" style={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusBarData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }} barSize={14}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                    <XAxis dataKey="status" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9 }} tickLine={false} axisLine={false} width={20} />
                    <Tooltip content={<StatusTooltip />} />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {statusBarData.map((entry, i) => (
                        <Cell key={i} fill={STATUS_COLORS[entry.status.toLowerCase()] || '#FFD400'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-3 mt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                {statusBarData.map(s => (
                  <div key={s.status} className="flex items-center gap-1.5">
                    <div className="w-2 h-2" style={{ backgroundColor: STATUS_COLORS[s.status.toLowerCase()] || '#FFD400', borderRadius: 1 }} />
                    <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.status}</span>
                    <span className="text-[11px] font-semibold text-white/60">{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
